import { Link, useLocation } from "react-router-dom"
import "./Header.scss"
import { MenuDrawer } from "../MenuDrawer"
import { useState, useEffect } from "react"
import { BurgerMenu } from "../BurgerMenu"
import classNames from "classnames"
import { getMenuOptions, MENU_OPTION_IDS } from "../../utils"
import { useTextStorage } from "../../hooks"
import { useMediaQuery } from "@mui/material"
import mainLogo from "../../assets/images/main-logo.svg"
import { motion, AnimatePresence } from "framer-motion"

export const Header: React.FC = () => {
  const [isDrawerOpen, setDrawerOpen] = useState(false)
  const [hasScrolled, setHasScrolled] = useState(false)
  const { pathname } = useLocation()
  const { ts } = useTextStorage()

  const handleBurgerClicked = () => {
    setDrawerOpen(true)
  }

  const handleDrawerClose = () => {
    setDrawerOpen(false)
  }
  const isMobile = useMediaQuery("(max-width: 900px)")
  const isHomePage = pathname === "/"

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY
      setHasScrolled(scrollPosition > 50)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const shouldShowLogo = (!isMobile && (!isHomePage || hasScrolled)) || isMobile

  const shouldHaveDropShadow = hasScrolled || !isHomePage

  return (
    <div
      className={classNames("header-outer-box", {
        "with-logo": shouldShowLogo,
        "has-drop-shadow": shouldHaveDropShadow,
        "has-scrolled": hasScrolled,
        "justify-start": isMobile,
        "mobile-version": isMobile,
      })}
    >
      {isMobile && (
        <BurgerMenu
          handleBurgerClicked={handleBurgerClicked}
          className="header-burger-menu"
        />
      )}
      <div className="header-desktop-menu-items-box">
        <HeaderDesktopMenuItem
          displayText={ts.menuOptions["about-me"]}
          linksTo="/about-me"
          isActive={pathname === "/about-me"}
        />
        <HeaderDesktopMenuItem
          displayText={ts.menuOptions.services}
          linksTo="/services"
          isActive={pathname === "/services"}
        />
        <HeaderDesktopMenuItem
          displayText={ts.menuOptions.gallery}
          linksTo="/gallery"
          isActive={pathname === "/gallery"}
        />
      </div>
      <AnimatePresence>
        {shouldShowLogo && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{
              opacity: 1,
              scale: 1,
              transition: { duration: 0.3, ease: "easeInOut" },
            }}
            exit={{
              opacity: 0,
              scale: 0.1,
              transition: { duration: 0.1, ease: "easeIn" },
            }}
            className={classNames("main-logo-container", {
              "mobile-version": isMobile,
            })}
          >
            <Link to="/">
              <img
                src={mainLogo}
                alt="main logo"
                className={classNames("main-logo", {
                  "decrease-size": isMobile && hasScrolled,
                })}
              />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="header-desktop-menu-items-box">
        <HeaderDesktopMenuItem
          displayText={ts.menuOptions.prices}
          linksTo="/#prices"
          isActive={false}
        />
        <HeaderDesktopMenuItem
          displayText={ts.menuOptions.connect}
          linksTo="/#connect"
          isActive={pathname === "/#connect"}
        />
        <HeaderDesktopMenuItem
          displayText={ts.menuOptions.faq}
          linksTo="/#faq"
          isActive={pathname === "/#faq"}
        />
      </div>
      <MenuDrawer
        isOpen={isDrawerOpen}
        onRequestClose={handleDrawerClose}
        menuOptions={getMenuOptions(MENU_OPTION_IDS, pathname, ts)}
      />
    </div>
  )
}

const HeaderDesktopMenuItem: React.FC<{
  displayText: string
  linksTo: string
  isActive?: boolean
}> = ({ displayText, linksTo, isActive = false }) => {
  if (!displayText || !linksTo) {
    return null
  }
  return (
    <div
      className={classNames("header-desktop-menu-item", { active: isActive })}
    >
      <Link to={linksTo}>{displayText.toUpperCase()}</Link>
    </div>
  )
}
